import { useEffect, useState } from "react";
import { useWebPlaceLookups } from "@/app/placeLookups";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu as MenuIcon, PanelLeft } from "lucide-react";
import { IconButton } from "@/ui";
import { hostMode } from "@/engine/transport";
import { useGlobalShortcuts } from "@/app/shortcuts";
import { useHostEvents } from "@/app/hostEvents";
import { useLayoutStore, useSelection } from "@/app/navigation";
import { NavSidebar } from "./NavSidebar";
import { useResizable } from "./useResizable";
import { ContactPreview } from "@/features/contact/ContactPreview";
import { CommandBar } from "@/features/commandbar/CommandBar";
import { AskDrawer } from "./AskDrawer";
import { ShortcutsModal } from "@/features/shortcuts/ShortcutsModal";
import { SendFeedbackDialog } from "@/features/feedback/SendFeedbackDialog";
import s from "./layout.module.css";

export function TitleBar() {
  return <div className={s.titleBar}>Hub</div>;
}

const NARROW = "(max-width: 720px)";

/** A phone-sized window: the sidebar becomes a menu that slides in, and a person opens as a page. */
function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(NARROW)?.matches);
  useEffect(() => {
    const m = typeof window !== "undefined" ? window.matchMedia?.(NARROW) : undefined;
    if (!m) return;
    const on = () => setNarrow(m.matches);
    m.addEventListener?.("change", on);
    return () => m.removeEventListener?.("change", on);
  }, []);
  return narrow;
}

export function Layout() {
  const mode = hostMode();
  const location = useLocation();
  const navigate = useNavigate();
  const navCollapsed = useLayoutStore((st) => st.navCollapsed);
  const toggleNav = useLayoutStore((st) => st.toggleNav);
  const sidebarContactId = useSelection((st) => st.sidebarContactId);
  const setSidebar = useSelection((st) => st.setSidebar);
  const nav = useResizable("hub_nav_width", 232, 180, 360);
  const side = useResizable("hub_sidebar_width", 360, 280, 560, -1);
  useGlobalShortcuts();
  useHostEvents();
  useWebPlaceLookups();

  // The right sidebar only makes sense next to a list; a person page already shows everything.
  const onContactPage = location.pathname.startsWith("/contact/");
  useEffect(() => {
    if (onContactPage && sidebarContactId) setSidebar(null);
  }, [onContactPage, sidebarContactId, setSidebar]);
  // Moving to another page closes the preview; it belongs to the list it was opened from.
  useEffect(() => {
    setSidebar(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const rail = mode === "extension";
  const narrow = useNarrow() && !rail;
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [location.pathname]);
  // No room for a preview next to the list: open the person's page instead.
  useEffect(() => {
    if (!narrow || !sidebarContactId || onContactPage) return;
    setSidebar(null);
    navigate(`/contact/${sidebarContactId}`);
  }, [narrow, sidebarContactId, onContactPage, setSidebar, navigate]);
  const showSidebar = !rail && !narrow && !!sidebarContactId && !onContactPage;

  return (
    <div className={s.shell} data-mode={mode}>
      {mode === "desktop" && <TitleBar />}
      <div className={s.panes}>
        <nav className={s.nav} data-collapsed={navCollapsed && !rail && !narrow ? "true" : undefined} data-rail={rail ? "true" : undefined} data-narrow={narrow ? "true" : undefined} data-open={narrow && navOpen ? "true" : undefined} style={narrow ? undefined : { width: nav.width }} aria-label="Primary">
          <NavSidebar rail={rail} />
        </nav>
        {narrow && navOpen && <div className={s.navScrim} onClick={() => setNavOpen(false)} aria-hidden />}
        {!rail && !narrow && !navCollapsed && <div className={s.resizer} data-active={nav.active ? "true" : undefined} onMouseDown={nav.onMouseDown} role="separator" aria-orientation="vertical" />}
        <main className={s.main}>
          {narrow && (
            <div className={s.narrowBar}>
              <IconButton label="Menu" onClick={() => setNavOpen(true)}>
                <MenuIcon size={18} />
              </IconButton>
              <span>Hub</span>
            </div>
          )}
          {navCollapsed && !rail && !narrow && (
            <span className={s.collapsedToggle}>
              <IconButton label="Show sidebar ([)" onClick={toggleNav}>
                <PanelLeft size={16} />
              </IconButton>
            </span>
          )}
          <Outlet />
        </main>
        {showSidebar && (
          <>
            <div className={s.resizer} data-active={side.active ? "true" : undefined} onMouseDown={side.onMouseDown} role="separator" aria-orientation="vertical" />
            <aside className={s.sidebar} style={{ width: side.width }} aria-label="Selected person">
              <ContactPreview contactId={sidebarContactId!} onClose={() => setSidebar(null)} />
            </aside>
          </>
        )}
      </div>
      <CommandBar />
      <ShortcutsModal />
      <SendFeedbackDialog />
      {!rail && <AskDrawer />}
    </div>
  );
}
